import { useEffect, useState } from "react";
import { api, apiError, fmtDate } from "./api";
import { Card, PageHead, Pill, Btn, Input, Select, Confirm, Field } from "./widgets";
import { toast } from "sonner";
import { X } from "lucide-react";
import { useAdmin } from "./AdminApp";

const ROLES = [
  { key: "super_admin", label: "Super Admin" },
  { key: "aviah_admin", label: "Aviah Admin / Owner" },
  { key: "editor", label: "Editor" },
  { key: "viewer", label: "Viewer" },
];

const roleLabel = (r) => ROLES.find((x) => x.key === r)?.label || r;

// Full-screen sheet on phones, centered dialog on desktop. User detail +
// management actions (management actions are super-admin only).
const UserSheet = ({ u, me, isSuper, onClose, onPatch, onReset, onDelete }) => (
  <div className="fixed inset-0 z-[80] bg-[#171717]/60 backdrop-blur-sm" role="dialog" aria-modal="true" data-testid="user-detail-sheet">
    <div className="flex h-[100dvh] w-full flex-col bg-white sm:mx-auto sm:my-[5dvh] sm:h-auto sm:max-h-[90dvh] sm:max-w-lg sm:rounded-[3px]">
      <div className="flex items-center justify-between border-b border-[#171717]/10 px-5 py-4 pt-[calc(1rem+env(safe-area-inset-top))]">
        <p className="text-[0.62rem] font-medium uppercase tracking-[0.18em] text-[#171717]/50">User detail</p>
        <button onClick={onClose} aria-label="Close" data-testid="user-detail-close"
          className="flex min-h-[44px] min-w-[44px] items-center justify-center text-[#171717]/60 hover:text-[#171717]">
          <X className="h-5 w-5" />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto px-5 py-6">
        <p className="text-xl font-light tracking-tight text-[#171717]">{u.name}</p>
        <p className="mt-1 break-all text-sm text-[#171717]/55">{u.email}</p>
        <div className="mt-4 flex flex-wrap gap-2">
          <Pill tone="champagne">{roleLabel(u.role)}</Pill>
          <Pill tone={u.active ? "green" : "red"}>{u.active ? "Active" : "Disabled"}</Pill>
        </div>
        <dl className="mt-6 space-y-2 border-t border-[#171717]/10 pt-5 text-sm">
          <div className="flex justify-between gap-4"><dt className="text-[#171717]/50">Last login</dt><dd className="text-right">{fmtDate(u.lastLogin)}</dd></div>
          <div className="flex justify-between gap-4"><dt className="text-[#171717]/50">Joined</dt><dd className="text-right">{fmtDate(u.createdAt)}</dd></div>
          <div className="flex justify-between gap-4"><dt className="text-[#171717]/50">Permissions</dt><dd className="text-right">{u.role === "super_admin" ? "Full access" : "Role-based"}</dd></div>
        </dl>
        {isSuper && u.id !== me.id && (
          <div className="mt-8 space-y-6 border-t border-[#171717]/10 pt-6">
            <Field label="Role">
              <Select className="w-full" value={u.role} onChange={(e) => onPatch(u.id, { role: e.target.value })} data-testid="user-detail-role">
                {ROLES.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
              </Select>
            </Field>
          </div>
        )}
      </div>
      {isSuper && (
        <div className="grid grid-cols-2 gap-2 border-t border-[#171717]/10 p-4 pb-[calc(1rem+env(safe-area-inset-bottom))]">
          <Btn onClick={() => onReset(u)} data-testid="user-detail-reset">Reset Access</Btn>
          {u.id !== me.id ? (
            <>
              <Btn onClick={() => onPatch(u.id, { active: !u.active })} data-testid="user-detail-toggle">
                {u.active ? "Disable" : "Enable"}
              </Btn>
              <Btn danger className="col-span-2" onClick={() => onDelete(u)} data-testid="user-detail-delete">Delete User</Btn>
            </>
          ) : (
            <span />
          )}
        </div>
      )}
    </div>
  </div>
);

const UsersPage = () => {
  const { user: me } = useAdmin();
  const isSuper = me.role === "super_admin";
  const [users, setUsers] = useState(null);
  const [invite, setInvite] = useState({ name: "", email: "", password: "", role: "viewer" });
  const [confirmDel, setConfirmDel] = useState(null);
  const [selected, setSelected] = useState(null);

  const load = () => api.get("/admin/users").then((r) => setUsers(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => {
    load();
  }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/users", invite);
      toast.success("User invited.");
      setInvite({ name: "", email: "", password: "", role: "viewer" });
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const patch = async (id, payload) => {
    try {
      await api.patch(`/admin/users/${id}`, payload);
      toast.success("User updated.");
      setSelected(null);
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const resetPassword = async (u) => {
    const pw = window.prompt(`New password for ${u.email} (min 10 characters):`);
    if (!pw) return;
    patch(u.id, { password: pw });
  };

  return (
    <div data-testid="users-page">
      <PageHead
        title="Users & Permissions"
        sub={isSuper
          ? "Roles are enforced on the server — hiding buttons is never the only protection."
          : "Team directory. Account management is reserved for the Super Admin."}
      />

      {isSuper && (
        <Card className="mb-6" data-testid="invite-user">
          <p className="mb-4 text-[0.62rem] font-medium uppercase tracking-[0.16em] text-[#171717]/50">Invite user</p>
          <form onSubmit={create} className="grid gap-3 sm:grid-cols-5">
            <Input placeholder="Full name" required value={invite.name} onChange={(e) => setInvite({ ...invite, name: e.target.value })} data-testid="invite-name" />
            <Input placeholder="Email" type="email" required value={invite.email} onChange={(e) => setInvite({ ...invite, email: e.target.value })} data-testid="invite-email" />
            <Input placeholder="Password (min 10)" type="text" required minLength={10} value={invite.password} onChange={(e) => setInvite({ ...invite, password: e.target.value })} data-testid="invite-password" />
            <Select value={invite.role} onChange={(e) => setInvite({ ...invite, role: e.target.value })} data-testid="invite-role">
              {ROLES.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
            </Select>
            <Btn primary type="submit" data-testid="invite-submit">Invite User</Btn>
          </form>
        </Card>
      )}

      {!users ? (
        <p className="text-sm text-[#171717]/50">Loading…</p>
      ) : (
        <>
          {/* Mobile: card list */}
          <div className="space-y-3 lg:hidden">
            {users.map((u) => (
              <Card key={u.id} data-testid={`user-card-${u.email}`}>
                <button className="block w-full text-left" onClick={() => setSelected(u)} data-testid={`user-open-${u.email}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm text-[#171717]">{u.name}</p>
                      <p className="mt-0.5 break-all text-xs text-[#171717]/50">{u.email}</p>
                      <p className="mt-1 text-[0.62rem] text-[#171717]/45">Last login: {fmtDate(u.lastLogin)}</p>
                    </div>
                    <div className="flex shrink-0 flex-col items-end gap-1.5">
                      <Pill tone="champagne">{roleLabel(u.role)}</Pill>
                      <Pill tone={u.active ? "green" : "red"}>{u.active ? "Active" : "Disabled"}</Pill>
                    </div>
                  </div>
                </button>
              </Card>
            ))}
          </div>

          {/* Desktop: full table */}
          <Card className="hidden overflow-x-auto p-0 lg:block">
            <table className="w-full min-w-[820px] text-sm">
              <thead>
                <tr className="border-b border-[#171717]/10 text-left text-[0.6rem] uppercase tracking-[0.16em] text-[#171717]/45">
                  <th className="px-5 py-3.5 font-medium">User</th>
                  <th className="px-5 py-3.5 font-medium">Role</th>
                  <th className="px-5 py-3.5 font-medium">Status</th>
                  <th className="px-5 py-3.5 font-medium">Last login</th>
                  {isSuper && <th className="px-5 py-3.5 font-medium">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="cursor-pointer border-b border-[#171717]/5 hover:bg-[#F4F0E8]/60" data-testid={`user-row-${u.email}`} onClick={() => setSelected(u)}>
                    <td className="px-5 py-3.5">
                      <p className="text-[#171717]">{u.name}</p>
                      <p className="text-xs text-[#171717]/50">{u.email}</p>
                    </td>
                    <td className="px-5 py-3.5" onClick={(e) => e.stopPropagation()}>
                      {isSuper ? (
                        <Select value={u.role} onChange={(e) => patch(u.id, { role: e.target.value })} disabled={u.id === me.id}>
                          {ROLES.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
                        </Select>
                      ) : (
                        <Pill tone="champagne">{roleLabel(u.role)}</Pill>
                      )}
                    </td>
                    <td className="px-5 py-3.5" onClick={(e) => e.stopPropagation()}>
                      {isSuper ? (
                        <button onClick={() => u.id !== me.id && patch(u.id, { active: !u.active })} data-testid={`user-active-${u.email}`}>
                          <Pill tone={u.active ? "green" : "red"}>{u.active ? "Active" : "Disabled"}</Pill>
                        </button>
                      ) : (
                        <Pill tone={u.active ? "green" : "red"}>{u.active ? "Active" : "Disabled"}</Pill>
                      )}
                    </td>
                    <td className="px-5 py-3.5 text-xs text-[#171717]/55">{fmtDate(u.lastLogin)}</td>
                    {isSuper && (
                      <td className="px-5 py-3.5" onClick={(e) => e.stopPropagation()}>
                        <div className="flex gap-2">
                          <Btn onClick={() => resetPassword(u)}>Reset Access</Btn>
                          {u.id !== me.id && <Btn danger onClick={() => setConfirmDel(u)}>Delete</Btn>}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </>
      )}

      {selected && (
        <UserSheet
          u={selected}
          me={me}
          isSuper={isSuper}
          onClose={() => setSelected(null)}
          onPatch={patch}
          onReset={resetPassword}
          onDelete={(u) => { setSelected(null); setConfirmDel(u); }}
        />
      )}

      <Confirm
        open={Boolean(confirmDel)}
        title="Delete this user?"
        body={`${confirmDel?.name} (${confirmDel?.email}) will lose all access immediately. This cannot be undone.`}
        confirmLabel="Delete user"
        onConfirm={async () => {
          try {
            await api.delete(`/admin/users/${confirmDel.id}`);
            toast.success("User deleted.");
            setConfirmDel(null);
            load();
          } catch (err) {
            toast.error(apiError(err));
            setConfirmDel(null);
          }
        }}
        onCancel={() => setConfirmDel(null)}
      />
    </div>
  );
};

export default UsersPage;
